import React, { useCallback, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Chip from "@mui/material/Chip";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Alert from "@mui/material/Alert";
import { DataGrid } from "@mui/x-data-grid";
import { toast } from "react-toastify";

import { ApiURLs } from "../../constant/ApiUrls";
import { fetchData, postData, patchData, deleteData } from "../../services/api";
import { cur } from "../../constant/Constants";

const money = (v) => `${cur}${Number(v || 0).toFixed(2)}`;
const shortDate = (v) => (v ? new Date(v).toLocaleDateString() : "-");
const forInput = (v) => (v ? new Date(v).toISOString().slice(0, 10) : "");

const SCOPES = [
  { value: "all", label: "Anything" },
  { value: "bump", label: "Bumps only" },
  { value: "spotlight", label: "Spotlight only" },
  { value: "plan", label: "Advert plans only" },
];

const BLANK = {
  code: "", discount_type: "percentage", discount_value: "",
  applies_to: "all", max_uses: "", min_spend: "",
  valid_from: "", valid_until: "", is_active: 1,
};

/**
 * Discount codes for listit.ie.
 *
 * These existed on listit.im and were never carried across when the Irish site
 * was branched off - no table, no controller, no routes. So on listit.ie a code
 * could not be entered and would have done nothing if it could.
 *
 * The value is always worked out on the server at the moment of payment, not
 * here. This screen only decides what the codes are.
 */
export default function DiscountCodes() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchData(`${ApiURLs.PROMOS}?search=${encodeURIComponent(search)}`);
      if (res.status === 1) setRows(res.data.result || []);
    } catch (e) { /* handled */ }
    setLoading(false);
  }, [search]);

  useEffect(() => {
    const t = setTimeout(load, search ? 350 : 0);
    return () => clearTimeout(t);
  }, [load, search]);

  const save = async () => {
    const body = {
      ...editing,
      discount_value: Number(editing.discount_value),
      max_uses: editing.max_uses === "" ? null : Number(editing.max_uses),
      min_spend: editing.min_spend === "" ? null : Number(editing.min_spend),
      valid_from: editing.valid_from || null,
      valid_until: editing.valid_until || null,
      is_active: editing.is_active ? 1 : 0,
    };
    try {
      const res = editing.id
        ? await patchData(`${ApiURLs.PROMOS}/${editing.id}`, body)
        : await postData(body, ApiURLs.PROMOS);
      if (res.status === 1) { setEditing(null); load(); }
    } catch (e) { /* handled */ }
  };

  const remove = async () => {
    const id = confirmDelete.id;
    setConfirmDelete(null);
    try {
      const res = await deleteData(`${ApiURLs.PROMOS}/${id}`);
      if (res.status === 1) { toast.success(res.message); load(); }
      else toast.error(res.message);
    } catch (e) { /* handled */ }
  };

  const columns = [
    { field: "code", headerName: "Code", width: 150 },
    {
      field: "discount_value", headerName: "Discount", width: 110,
      renderCell: (p) => (p.row.discount_type === "percentage" ? `${Number(p.value)}%` : money(p.value)),
    },
    {
      field: "applies_to", headerName: "Applies to", width: 140,
      renderCell: (p) => (SCOPES.find((s) => s.value === p.value) || {}).label || p.value,
    },
    { field: "min_spend", headerName: "Min spend", width: 110, renderCell: (p) => (p.value ? money(p.value) : "-") },
    {
      field: "used_count", headerName: "Used", width: 110,
      renderCell: (p) => `${p.value}${p.row.max_uses ? ` / ${p.row.max_uses}` : ""}`,
    },
    { field: "discount_given", headerName: "Given away", width: 120, renderCell: (p) => money(p.value) },
    { field: "valid_from", headerName: "From", width: 110, renderCell: (p) => shortDate(p.value) },
    { field: "valid_until", headerName: "Until", width: 110, renderCell: (p) => shortDate(p.value) },
    {
      field: "is_active", headerName: "Status", width: 110,
      renderCell: (p) => (
        <Chip size="small" label={Number(p.value) ? "Live" : "Off"} color={Number(p.value) ? "success" : "default"} />
      ),
    },
    {
      field: "_actions", headerName: "", width: 160, sortable: false,
      renderCell: (p) => (
        <>
          <Button size="small" onClick={() => setEditing({ ...p.row, valid_from: forInput(p.row.valid_from), valid_until: forInput(p.row.valid_until) })}>Edit</Button>
          <Button size="small" color="error" onClick={() => setConfirmDelete(p.row)}>Delete</Button>
        </>
      ),
    },
  ];

  return (
    <Box sx={{ height: "95%", width: "100%", display: "flex", flexDirection: "column", gap: 2 }}>
      <Paper sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, p: 2, flexWrap: "wrap" }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Discount codes</Typography>
          <TextField size="small" placeholder="Search a code" value={search}
            onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 220 }} />
          <Box sx={{ flex: 1 }} />
          <Button variant="contained" onClick={() => setEditing({ ...BLANK })}>New code</Button>
        </Box>

        <DataGrid
          rows={(rows || []).map((r) => ({ id: r.id, ...r }))}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          pageSizeOptions={[25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          sx={{ border: 0 }}
        />
      </Paper>

      <Dialog open={!!editing} onClose={() => setEditing(null)} fullWidth maxWidth="sm">
        <DialogTitle>{editing && editing.id ? `Edit ${editing.code}` : "New discount code"}</DialogTitle>
        <DialogContent sx={{ display: "flex", flexWrap: "wrap", gap: 2, pt: 1 }}>
          {editing && (
            <>
              <TextField label="Code" size="small" sx={{ flex: "1 1 200px" }}
                value={editing.code}
                onChange={(e) => setEditing({ ...editing, code: e.target.value.toUpperCase() })}
                helperText="Letters, numbers, dashes" />
              <TextField label="Type" size="small" select sx={{ flex: "1 1 160px" }}
                value={editing.discount_type}
                onChange={(e) => setEditing({ ...editing, discount_type: e.target.value })}>
                <MenuItem value="percentage">Percentage off</MenuItem>
                <MenuItem value="fixed">Fixed amount off</MenuItem>
              </TextField>
              <TextField label={editing.discount_type === "percentage" ? "Percent off" : `Amount off (${cur})`}
                size="small" type="number" sx={{ flex: "1 1 160px" }}
                value={editing.discount_value}
                onChange={(e) => setEditing({ ...editing, discount_value: e.target.value })} />
              <TextField label="Applies to" size="small" select sx={{ flex: "1 1 200px" }}
                value={editing.applies_to || "all"}
                onChange={(e) => setEditing({ ...editing, applies_to: e.target.value })}>
                {SCOPES.map((s) => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
              </TextField>
              <TextField label="Maximum uses" size="small" type="number" sx={{ flex: "1 1 160px" }}
                value={editing.max_uses === null ? "" : editing.max_uses}
                onChange={(e) => setEditing({ ...editing, max_uses: e.target.value })}
                helperText="Blank for unlimited" />
              <TextField label={`Minimum spend (${cur})`} size="small" type="number" sx={{ flex: "1 1 160px" }}
                value={editing.min_spend === null ? "" : editing.min_spend}
                onChange={(e) => setEditing({ ...editing, min_spend: e.target.value })}
                helperText="Blank for none" />
              <TextField label="Live from" size="small" type="date" sx={{ flex: "1 1 160px" }}
                InputLabelProps={{ shrink: true }}
                value={editing.valid_from || ""}
                onChange={(e) => setEditing({ ...editing, valid_from: e.target.value })} />
              <TextField label="Live until" size="small" type="date" sx={{ flex: "1 1 160px" }}
                InputLabelProps={{ shrink: true }}
                value={editing.valid_until || ""}
                onChange={(e) => setEditing({ ...editing, valid_until: e.target.value })} />
              <FormControlLabel sx={{ flex: "1 1 100%" }}
                control={<Switch checked={!!Number(editing.is_active)}
                  onChange={(e) => setEditing({ ...editing, is_active: e.target.checked ? 1 : 0 })} />}
                label="Live" />
              {editing.id && Number(editing.used_count) > 0 && (
                <Alert severity="info" sx={{ flex: "1 1 100%" }}>
                  This code has already been used {editing.used_count} time(s). Changing the discount does not
                  change what those customers were charged.
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)}>Cancel</Button>
          <Button variant="contained" onClick={save}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Delete {confirmDelete && confirmDelete.code}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {confirmDelete && Number(confirmDelete.redemptions) > 0
              ? `This code has been used ${confirmDelete.redemptions} time(s), so it will be switched off rather than deleted. The payment history still needs it to add up.`
              : "Nobody has used this code, so it can be removed completely."}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={remove}>
            {confirmDelete && Number(confirmDelete.redemptions) > 0 ? "Switch it off" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
